import { api, withAccount, requireAccount } from "./core";

// GalaxyShare is one owner-created grant (the OWNER's view of a row): the galaxy is
// shared read-only with the GitHub identity in `target_ex_github_id`.
// `target_github_login` is the denormalized handle for display.
export interface GalaxyShare {
  id: number;
  account_id: number;
  created_by: number;
  target_ex_github_id: number;
  target_github_login: string;
  label: string;
  status: string; // "active" | "revoked"
  disclosure: string; // "anonymous" | "full" — how members appear to this recipient
  revoked_at: string | null;
  created_at: string;
  updated_at: string;
}

// SharedGalaxyView is the RECIPIENT's projection of a galaxy shared with them — only
// the identity (login/type), never any observation figure. Feeds the switcher's
// third "Shared" category. Matches the backend's usecase.SharedGalaxyView.
export interface SharedGalaxyView {
  account_id: number;
  account_login: string;
  account_type: string; // "personal" | "org"
}

// GitHubUserHit is one autocomplete candidate for the invite input: a login to
// grant to + an avatar for the dropdown. Identity only.
export interface GitHubUserHit {
  login: string;
  avatar_url: string;
}

// UserSearchResponse is the invite-autocomplete payload. reauth_required=true means
// the caller's GitHub authorization is stale/absent (not that nobody matched), so
// the dropdown should offer a "reconnect GitHub" CTA instead of a bare "no results".
// Optional so a pre-flag backend (empty {users}) still parses as reauth_required=false.
export interface UserSearchResponse {
  users: GitHubUserHit[];
  reauth_required?: boolean;
}

// GalaxyReadAuditReader is one shared guest's rolled-up read history of a galaxy
// (D-04 accountability): who read it, how many surface reads, and when last.
export interface GalaxyReadAuditReader {
  viewer_login: string;
  viewer_ex_github_id: number;
  read_count: number;
  last_accessed_at: string;
}

// sharedGalaxiesApi — the Shared Galaxies surface (docs/design/shared-galaxies.md).
// Owner calls are galaxy-scoped (?account_login= via withAccount, the URL galaxy is
// the source of truth); recipient calls are user-scoped by the JWT (no account_login).
export const sharedGalaxiesApi = {
  // Owner (admin, galaxy-scoped) — grant to a GitHub login / list / revoke.
  list: (accountLogin: string) =>
    api.get<{ shares: GalaxyShare[] }>(withAccount("/shared-galaxies", requireAccount(accountLogin, "sharedGalaxiesApi.list"))),
  // create grants the galaxy to a GitHub identity by login. The backend resolves the
  // login to a stable id; a 404 means "no such GitHub user".
  create: (accountLogin: string, githubLogin: string, label = "", disclosure = "anonymous") =>
    api.post<GalaxyShare>(withAccount("/shared-galaxies", requireAccount(accountLogin, "sharedGalaxiesApi.create")), {
      github_login: githubLogin,
      label,
      disclosure,
    }),
  revoke: (accountLogin: string, id: number) =>
    api.delete<void>(withAccount(`/shared-galaxies/${id}`, requireAccount(accountLogin, "sharedGalaxiesApi.revoke"))),
  // readAudit is the D-04 accountability view — who from outside (shared guests) read
  // this galaxy, rolled up per identity (count + last read). Admin-only, galaxy-scoped.
  readAudit: (accountLogin: string) =>
    api.get<{ readers: GalaxyReadAuditReader[] }>(
      withAccount("/shared-galaxies/read-audit", requireAccount(accountLogin, "sharedGalaxiesApi.readAudit")),
    ),
  // searchUsers returns GitHub login suggestions for the invite input, searched with
  // the caller's own token server-side. Always resolves to a list (empty on any hiccup).
  searchUsers: (accountLogin: string, q: string) =>
    api.get<UserSearchResponse>(
      withAccount("/shared-galaxies/user-search", requireAccount(accountLogin, "sharedGalaxiesApi.searchUsers"), { q }),
    ),

  // Recipient (user-scoped) — list galaxies shared with me (auto-matched by my
  // GitHub identity), self-add an OPEN galaxy by login, leave one.
  sharedWithMe: () => api.get<{ galaxies: SharedGalaxyView[] }>("/me/shared-galaxies"),
  // selfAdd pulls an OPEN galaxy (its admin turned Open Share on) into my own
  // shared list by typing its login. 404 = no such galaxy; 403 = not open; 400 =
  // it's already mine. Returns the galaxy's switcher view so I can navigate in.
  selfAdd: (login: string) => api.post<SharedGalaxyView>("/me/shared-galaxies", { login }),
  // searchGalaxies returns GitHub login suggestions for the self-add input, searched
  // with the caller's own token server-side (user-scoped). Always resolves to a list.
  searchGalaxies: (q: string) =>
    api.get<UserSearchResponse>(`/me/shared-galaxies/user-search?q=${encodeURIComponent(q)}`),
  // leave drops the caller's OWN share for a galaxy, keyed by the galaxy's
  // account_id. Re-access needs the owner to share again.
  leave: (galaxyAccountId: number) => api.delete<void>(`/me/shared-galaxies/${galaxyAccountId}`),
};
